import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { usePathname } from 'next/navigation';

// Register ScrollTrigger plugin
if (typeof window !== 'undefined') {
	gsap.registerPlugin(ScrollTrigger);
}

export const useFadeUpAnimation = () => {
	const pathname = usePathname();

	useEffect(() => {
		const elements = gsap.utils.toArray('.fade-up');
		const observers = [];
		const animations = [];
		
		// Check if we need to wait for preloader or can initialize immediately
		const checkAndInitialize = () => {
			// Check if body overflow is hidden (preloader active)
			const isPreloaderActive = () => {
				if (typeof document === 'undefined') return false;
				const body = document.body;
				const computedStyle = window.getComputedStyle(body);
				return computedStyle.overflow === 'hidden';
			};

			const initializeAnimations = () => {
				elements.forEach(el => {
					// Set initial state immediately
					gsap.set(el, { y: 50, opacity: 0 });
					
					const animation = gsap.to(el, {
						y: 0,
						opacity: 1,
						duration: 1.2,
						ease: 'power3.out',
						paused: true,
					});
					animations.push(animation);

					const observer = new IntersectionObserver(
						entries => {
							entries.forEach(entry => {
								if (entry.isIntersecting) {
									animation.play();
								} else {
									animation.reverse();
								}
							});
						},
						{ threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
					);
					observer.observe(el);
					observers.push(observer);
				});
			};

			// If preloader is active, wait for it to complete
			if (isPreloaderActive()) {
				const checkInterval = setInterval(() => {
					if (!isPreloaderActive()) {
						clearInterval(checkInterval);
						// Add delay to ensure preloader animation completes
						setTimeout(initializeAnimations, 500);
					}
				}, 100);
				
				// Cleanup interval on component unmount
				return () => clearInterval(checkInterval);
			} else {
				// Initialize immediately if no preloader
				setTimeout(initializeAnimations, 100);
			}
		};
		
		// Initialize with small delay to ensure DOM is ready
		const initTimeout = setTimeout(checkAndInitialize, 50);

		return () => {
			clearTimeout(initTimeout);
			// Disconnect all observers
			observers.forEach(observer => observer.disconnect());
			// Kill all animations
			animations.forEach(animation => animation.kill());
		};
	}, [pathname]);
};

// Advanced text reveal animation (like Obys)
export const useTextRevealAnimation = () => {
	const pathname = usePathname();

	useEffect(() => {
		const textElements = gsap.utils.toArray('.text-reveal');
		
		textElements.forEach(el => {
			// Split text into spans for character animation
			const text = el.textContent;
			el.innerHTML = '';
			
			text.split('').forEach(char => {
				const span = document.createElement('span');
				span.textContent = char === ' ' ? '\u00A0' : char;
				span.style.display = 'inline-block';
				span.style.transform = 'translateY(100%)';
				span.style.opacity = '0';
				el.appendChild(span);
			});

			const chars = el.querySelectorAll('span');
			
			ScrollTrigger.create({
				trigger: el,
				start: 'top 80%',
				onEnter: () => {
					gsap.to(chars, {
						y: 0,
						opacity: 1,
						duration: 0.8,
						ease: 'power3.out',
						stagger: 0.02
					});
				}
			});
		});

		return () => {
			ScrollTrigger.getAll().forEach(trigger => trigger.kill());
		};
	}, [pathname]);
};

// Advanced image reveal animation
export const useImageRevealAnimation = () => {
	const pathname = usePathname();

	useEffect(() => {
		const imageElements = gsap.utils.toArray('.image-reveal');
		
		imageElements.forEach(el => {
			gsap.set(el, {
				scale: 1.3,
				opacity: 0,
				filter: 'blur(20px)'
			});

			ScrollTrigger.create({
				trigger: el,
				start: 'top 85%',
				onEnter: () => {
					gsap.to(el, {
						scale: 1,
						opacity: 1,
						filter: 'blur(0px)',
						duration: 2.0,
						ease: 'power4.out'
					});
				}
			});
		});

		return () => {
			ScrollTrigger.getAll().forEach(trigger => trigger.kill());
		};
	}, [pathname]);
};
